import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createTestSupportTicket,
  listSupportSites,
  listSupportTickets,
  updateSupportTicketStatus,
  type AdminSupportTicket,
  type SupportTicketStatus,
} from '../api/admin';

const STATUSES: Array<{ value: SupportTicketStatus; label: string }> = [
  { value: 'open', label: 'Open' },
  { value: 'dispatched', label: 'Dispatched' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
];

const STATUS_CLASSES: Record<SupportTicketStatus, string> = {
  open: 'border-amber-200 bg-amber-50 text-amber-800',
  dispatched: 'border-blue-200 bg-blue-50 text-blue-800',
  in_progress: 'border-purple-200 bg-purple-50 text-purple-800',
  resolved: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  closed: 'border-slate-200 bg-slate-100 text-slate-700',
};

function statusLabel(status: SupportTicketStatus): string {
  return STATUSES.find((item) => item.value === status)?.label ?? status;
}

function channelLabel(channel: string): string {
  return channel === 'internal' ? 'GuideAI Support Ticket' : channel.replace(/_/g, ' ');
}

function formatDate(value: string): string {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

function StatusBadge({ status }: { status: SupportTicketStatus }) {
  return (
    <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${STATUS_CLASSES[status]}`}>
      {statusLabel(status)}
    </span>
  );
}

async function copyToClipboard(value: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand('copy');
  textarea.remove();
  if (!copied) throw new Error('Clipboard copy failed');
}

function TicketDetail({ ticket }: { ticket: AdminSupportTicket }) {
  const queryClient = useQueryClient();
  const copyResetRef = useRef<number | null>(null);
  const [copied, setCopied] = useState<'request' | 'body' | null>(null);
  const [copyError, setCopyError] = useState(false);
  const requestValue = ticket.channel_response.request;
  const request = requestValue && typeof requestValue === 'object' && !Array.isArray(requestValue)
    ? requestValue as Record<string, unknown>
    : null;
  const response = { ...ticket.channel_response };
  delete response.request;
  const copyRequest = async (kind: 'request' | 'body') => {
    if (!request) return;
    const value = kind === 'body'
      ? request.body ?? null
      : {
          name: 'Escalate to support',
          url: request.url ?? '',
          method: request.method ?? 'POST',
          headers: request.headers ?? {},
          body: request.body ?? null,
        };
    try {
      await copyToClipboard(JSON.stringify(value, null, 2));
      setCopyError(false);
      setCopied(kind);
      if (copyResetRef.current) window.clearTimeout(copyResetRef.current);
      copyResetRef.current = window.setTimeout(() => setCopied(null), 2500);
    } catch {
      setCopied(null);
      setCopyError(true);
    }
  };

  useEffect(() => {
    setCopied(null);
    setCopyError(false);
    return () => {
      if (copyResetRef.current) window.clearTimeout(copyResetRef.current);
    };
  }, [ticket.id]);
  const statusMutation = useMutation({
    mutationFn: (status: SupportTicketStatus) => updateSupportTicketStatus(ticket.id, status),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'support-tickets'] }),
  });

  return (
    <section aria-labelledby="ticket-detail-title" className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-200 p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <StatusBadge status={ticket.status} />
              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium capitalize text-slate-600">
                {channelLabel(ticket.channel)}
              </span>
            </div>
            <h2 id="ticket-detail-title" className="text-lg font-semibold text-slate-950">
              {ticket.summary}
            </h2>
            <p className="mt-1 font-mono text-xs text-slate-500">{ticket.id}</p>
          </div>
          <div>
            <label htmlFor="ticket-status" className="block text-xs font-semibold text-slate-600">
              Ticket status
            </label>
            <select
              id="ticket-status"
              value={ticket.status}
              disabled={statusMutation.isPending}
              onChange={(event) => statusMutation.mutate(event.target.value as SupportTicketStatus)}
              className="mt-1 min-h-11 cursor-pointer rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-200 disabled:cursor-wait disabled:opacity-60"
            >
              {STATUSES.map((status) => <option key={status.value} value={status.value}>{status.label}</option>)}
            </select>
          </div>
        </div>
        {statusMutation.isError && (
          <p role="alert" className="mt-3 text-sm text-rose-700">The ticket status could not be updated.</p>
        )}
      </div>

      <div className="space-y-6 p-5">
        <dl className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
          <div><dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Site</dt><dd className="mt-1 text-slate-900">{ticket.site_name} <span className="text-slate-500">({ticket.site_domain})</span></dd></div>
          <div><dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Organization</dt><dd className="mt-1 text-slate-900">{ticket.organization_name ?? 'No organization'}</dd></div>
          <div><dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Created</dt><dd className="mt-1 text-slate-900">{formatDate(ticket.created_at)}</dd></div>
          <div><dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Trigger</dt><dd className="mt-1 capitalize text-slate-900">{ticket.trigger.replace(/_/g, ' ')}</dd></div>
          <div><dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Visitor</dt><dd className="mt-1 break-all font-mono text-xs text-slate-900">{ticket.visitor_id ?? 'Unknown'}</dd></div>
          <div><dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Page</dt><dd className="mt-1 break-all text-slate-900">{ticket.page_url ? <a href={ticket.page_url} target="_blank" rel="noreferrer" className="text-purple-700 underline decoration-purple-200 underline-offset-2 hover:decoration-purple-600">{ticket.page_url}</a> : 'Not supplied'}</dd></div>
        </dl>

        <div>
          <h3 className="text-sm font-semibold text-slate-900">User message</h3>
          <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm leading-6 text-slate-800">
            {ticket.user_message ?? 'No user message was recorded.'}
          </div>
        </div>

        <div>
          <h3 className="text-sm font-semibold text-slate-900">Conversation</h3>
          <div className="mt-2 space-y-2">
            {ticket.conversation_history.length === 0 && <p className="text-sm text-slate-500">No conversation history was recorded.</p>}
            {ticket.conversation_history.map((message, index) => (
              <div key={`${ticket.id}-message-${index}`} className="rounded-lg border border-slate-200 p-3">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{String(message.role ?? 'unknown')}</div>
                <p className="mt-1 whitespace-pre-wrap text-sm leading-6 text-slate-800">{String(message.content ?? '')}</p>
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold text-slate-900">Outbound request</h3>
              <p className="mt-0.5 text-xs text-slate-500">Copy this JSON and paste it into the Copilot escalation tool.</p>
            </div>
            {request && (
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={() => copyRequest('body')} className="inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-purple-300">
                  {copied === 'body' ? (
                    <svg className="h-4 w-4 text-emerald-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="m5 13 4 4L19 7" /></svg>
                  ) : (
                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><rect x="9" y="9" width="11" height="11" rx="2" /><path strokeLinecap="round" strokeLinejoin="round" d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
                  )}
                  {copied === 'body' ? 'Body copied' : 'Copy body'}
                </button>
                <button type="button" onClick={() => copyRequest('request')} className="inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-lg bg-purple-700 px-3 text-xs font-semibold text-white transition-colors hover:bg-purple-800 focus:outline-none focus:ring-2 focus:ring-purple-300">
                  {copied === 'request' ? (
                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="m5 13 4 4L19 7" /></svg>
                  ) : (
                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><rect x="9" y="9" width="11" height="11" rx="2" /><path strokeLinecap="round" strokeLinejoin="round" d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
                  )}
                  {copied === 'request' ? 'Request copied' : 'Copy request JSON'}
                </button>
              </div>
            )}
          </div>
          <div aria-live="polite" className="mt-2 min-h-5 text-xs">
            {copied && <span className="font-medium text-emerald-700">Copied safely. Replace any [REDACTED] values before using authentication.</span>}
            {copyError && <span role="alert" className="font-medium text-rose-700">Clipboard access failed. Select the JSON manually and copy it.</span>}
          </div>
          {request ? (
            <div className="mt-2 overflow-hidden rounded-xl border border-slate-700 bg-slate-950">
              <div className="flex flex-wrap items-center gap-2 border-b border-slate-700 px-4 py-3">
                <span className="rounded-md bg-emerald-500/15 px-2 py-1 font-mono text-xs font-bold text-emerald-300">
                  {String(request.method ?? 'POST')}
                </span>
                <span className="min-w-0 break-all font-mono text-xs text-slate-300">
                  {String(request.url ?? 'Unknown endpoint')}
                </span>
              </div>
              <div className="grid grid-cols-1 divide-y divide-slate-700 lg:grid-cols-2 lg:divide-x lg:divide-y-0">
                <div className="min-w-0 p-4">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Request headers</div>
                  <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap break-words font-mono text-xs leading-5 text-slate-100">{JSON.stringify(request.headers ?? {}, null, 2)}</pre>
                </div>
                <div className="min-w-0 p-4">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Request body</div>
                  <pre className="mt-2 max-h-96 overflow-auto whitespace-pre-wrap break-words font-mono text-xs leading-5 text-slate-100">{JSON.stringify(request.body ?? null, null, 2)}</pre>
                </div>
              </div>
            </div>
          ) : (
            <p className="mt-2 rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
              No request snapshot was recorded for this ticket. New webhook attempts include one automatically.
            </p>
          )}
        </div>

        <div>
          <h3 className="text-sm font-semibold text-slate-900">Dispatch response</h3>
          <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-slate-950 p-4 font-mono text-xs leading-5 text-slate-100">
            {JSON.stringify(response, null, 2)}
          </pre>
        </div>
      </div>
    </section>
  );
}

export function SupportTicketsPage() {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState('');
  const [siteId, setSiteId] = useState('');
  const [searchDraft, setSearchDraft] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [testSiteId, setTestSiteId] = useState('');

  const sitesQuery = useQuery({
    queryKey: ['admin', 'support-sites'],
    queryFn: listSupportSites,
  });
  const ticketsQuery = useQuery({
    queryKey: ['admin', 'support-tickets', status, siteId, search, page],
    queryFn: () => listSupportTickets({
      status: status || undefined,
      site_id: siteId || undefined,
      search: search || undefined,
      page,
      page_size: 30,
    }),
  });
  const testMutation = useMutation({
    mutationFn: () => createTestSupportTicket(testSiteId),
    onSuccess: (ticket) => {
      setSelectedId(ticket.id);
      setStatus('');
      setSiteId('');
      setSearch('');
      setSearchDraft('');
      setPage(1);
      queryClient.invalidateQueries({ queryKey: ['admin', 'support-tickets'] });
    },
  });

  const tickets = ticketsQuery.data?.items ?? [];
  const selected = tickets.find((ticket) => ticket.id === selectedId) ?? null;
  const counts = ticketsQuery.data?.status_counts ?? {};
  const totalTickets = useMemo(
    () => Object.values(counts).reduce((sum, count) => sum + count, 0),
    [counts],
  );

  useEffect(() => {
    if (!selectedId && tickets.length > 0) setSelectedId(tickets[0].id);
  }, [selectedId, tickets]);

  const applySearch = (event: FormEvent) => {
    event.preventDefault();
    setSearch(searchDraft.trim());
    setPage(1);
    setSelectedId(null);
  };

  return (
    <div className="mx-auto max-w-[1500px] p-5 sm:p-8">
      <header className="flex flex-col justify-between gap-5 lg:flex-row lg:items-end">
        <div>
          <h1 className="text-3xl font-bold text-slate-950">Support Tickets</h1>
          <p className="mt-2 text-slate-600">Inspect escalation payloads, dispatch results, conversations, and ticket status.</p>
        </div>
        <div className="rounded-xl border border-purple-200 bg-purple-50 p-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
            <div>
              <label htmlFor="test-ticket-site" className="block text-xs font-semibold text-purple-900">Sample ticket site</label>
              <select id="test-ticket-site" value={testSiteId} onChange={(event) => setTestSiteId(event.target.value)} className="mt-1 min-h-11 min-w-64 cursor-pointer rounded-lg border border-purple-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300">
                <option value="">Select a site</option>
                {(sitesQuery.data ?? []).map((site) => <option key={site.id} value={site.id}>{site.name} · {site.domain}</option>)}
              </select>
            </div>
            <button type="button" onClick={() => testMutation.mutate()} disabled={!testSiteId || testMutation.isPending} className="min-h-11 cursor-pointer rounded-lg bg-purple-700 px-4 text-sm font-semibold text-white transition-colors hover:bg-purple-800 focus:outline-none focus:ring-2 focus:ring-purple-400 disabled:cursor-not-allowed disabled:opacity-50">
              {testMutation.isPending ? 'Creating...' : 'Create sample ticket'}
            </button>
          </div>
          <p className="mt-2 text-xs text-purple-800">Creates an internal sample only. It does not call the configured webhook.</p>
          {testMutation.isError && <p role="alert" className="mt-2 text-xs font-semibold text-rose-700">The sample ticket could not be created.</p>}
        </div>
      </header>

      <section aria-label="Ticket summary" className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-6">
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"><div className="text-xs font-semibold uppercase tracking-wide text-slate-500">All tickets</div><div className="mt-1 text-2xl font-bold text-slate-950">{totalTickets}</div></div>
        {STATUSES.map((item) => <div key={item.value} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"><div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{item.label}</div><div className="mt-1 text-2xl font-bold text-slate-950">{counts[item.value] ?? 0}</div></div>)}
      </section>

      <section aria-label="Ticket filters" className="mt-6 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,1fr)_220px_260px]">
          <form onSubmit={applySearch} className="flex gap-2">
            <div className="flex-1">
              <label htmlFor="ticket-search" className="sr-only">Search support tickets</label>
              <input id="ticket-search" type="search" value={searchDraft} onChange={(event) => setSearchDraft(event.target.value)} placeholder="Search summary, message, or visitor ID" className="min-h-11 w-full rounded-lg border border-slate-300 px-3 text-sm focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-200" />
            </div>
            <button type="submit" className="min-h-11 cursor-pointer rounded-lg border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-purple-200">Search</button>
          </form>
          <div>
            <label htmlFor="ticket-status-filter" className="sr-only">Filter by status</label>
            <select id="ticket-status-filter" value={status} onChange={(event) => { setStatus(event.target.value); setPage(1); setSelectedId(null); }} className="min-h-11 w-full cursor-pointer rounded-lg border border-slate-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-200">
              <option value="">All statuses</option>
              {STATUSES.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="ticket-site-filter" className="sr-only">Filter by site</label>
            <select id="ticket-site-filter" value={siteId} onChange={(event) => { setSiteId(event.target.value); setPage(1); setSelectedId(null); }} className="min-h-11 w-full cursor-pointer rounded-lg border border-slate-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-200">
              <option value="">All sites</option>
              {(sitesQuery.data ?? []).map((site) => <option key={site.id} value={site.id}>{site.name} · {site.domain}</option>)}
            </select>
          </div>
        </div>
      </section>

      {ticketsQuery.isLoading && <div className="py-16 text-center text-sm text-slate-500">Loading support tickets...</div>}
      {ticketsQuery.isError && <div role="alert" className="mt-6 rounded-xl border border-rose-200 bg-rose-50 p-6 text-center text-sm text-rose-700">Support tickets could not be loaded.</div>}
      {!ticketsQuery.isLoading && !ticketsQuery.isError && tickets.length === 0 && (
        <div className="mt-6 rounded-xl border border-dashed border-slate-300 bg-white p-12 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-purple-50 text-purple-700">
            <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h3m-5 5h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
          </div>
          <h2 className="mt-4 text-base font-semibold text-slate-900">No tickets match these filters</h2>
          <p className="mt-1 text-sm text-slate-500">Create a sample ticket above or escalate from the installed SDK.</p>
        </div>
      )}

      {tickets.length > 0 && (
        <div className="mt-6 grid grid-cols-1 items-start gap-5 xl:grid-cols-[minmax(360px,0.8fr)_minmax(0,1.2fr)]">
          <section aria-label="Ticket list" className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 px-4 py-3 text-sm text-slate-600">{ticketsQuery.data?.total ?? 0} matching ticket{ticketsQuery.data?.total === 1 ? '' : 's'}</div>
            <div className="divide-y divide-slate-100">
              {tickets.map((ticket) => (
                <button key={ticket.id} type="button" onClick={() => setSelectedId(ticket.id)} aria-pressed={selectedId === ticket.id} className={`w-full cursor-pointer p-4 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-purple-300 ${selectedId === ticket.id ? 'bg-purple-50' : 'bg-white hover:bg-slate-50'}`}>
                  <div className="flex items-start justify-between gap-3"><StatusBadge status={ticket.status} /><span className="text-xs text-slate-500">{formatDate(ticket.created_at)}</span></div>
                  <div className="mt-3 line-clamp-2 text-sm font-semibold leading-5 text-slate-900">{ticket.summary}</div>
                  <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500"><span>{ticket.site_name}</span><span aria-hidden="true">·</span><span className="capitalize">{channelLabel(ticket.channel)}</span><span aria-hidden="true">·</span><span>{ticket.visitor_id ?? 'Unknown visitor'}</span></div>
                </button>
              ))}
            </div>
            {(ticketsQuery.data?.pages ?? 1) > 1 && (
              <div className="flex items-center justify-between border-t border-slate-200 p-3">
                <span className="text-xs text-slate-500">Page {page} of {ticketsQuery.data?.pages}</span>
                <div className="flex gap-2">
                  <button type="button" onClick={() => { setPage((value) => Math.max(1, value - 1)); setSelectedId(null); }} disabled={page === 1} className="min-h-10 cursor-pointer rounded-lg border border-slate-200 px-3 text-sm disabled:cursor-not-allowed disabled:opacity-40">Previous</button>
                  <button type="button" onClick={() => { setPage((value) => Math.min(ticketsQuery.data?.pages ?? 1, value + 1)); setSelectedId(null); }} disabled={page >= (ticketsQuery.data?.pages ?? 1)} className="min-h-10 cursor-pointer rounded-lg border border-slate-200 px-3 text-sm disabled:cursor-not-allowed disabled:opacity-40">Next</button>
                </div>
              </div>
            )}
          </section>
          {selected ? <TicketDetail ticket={selected} /> : <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-500">Select a ticket to inspect its details.</div>}
        </div>
      )}
    </div>
  );
}
